// A table's sort, filters and cursor live in the query string, so a view is a link that can be
// sent to someone and the browser's back button works through paging.
//
// `createTableUrl` builds the reader and writers for one table from the only things that differ
// between tables: which columns sort, which sort is the default, and which filters exist. The
// component reads the query from the URL and writes it back; it does not keep a mirrored copy in
// component state, because two copies of one thing is how a URL and a screen end up disagreeing.
//
// A table may also have list-valued filters (`multiFilters`): a set of accepted values per
// column, carried as `f.<key>=<value>` repeated, the empty value standing for a blank cell. The
// tracker uses them (TX-US-0020); the registers' single-valued filters are unchanged.

const FILTER_PREFIX = 'f.';

export function createTableUrl({ columns, defaultSort, filters = [], multiFilters = [] }) {
  const sortKeys = new Set(columns.map((column) => column.key));
  const multiKeys = new Set(multiFilters);
  const multi = multiKeys.size > 0;

  const defaultDirectionFor = (sort) =>
    columns.find((column) => column.key === sort)?.defaultDirection ?? 'asc';

  const defaultQuery = Object.freeze({
    sort: defaultSort,
    direction: defaultDirectionFor(defaultSort),
    ...Object.fromEntries(filters.map((name) => [name, null])),
    ...(multi ? { filters: Object.freeze({}) } : {}),
    cursor: null,
  });

  // The `f.<key>` parameters, one per accepted value, for the keys this table filters on. A key
  // it does not know is dropped for the same reason an unsortable sort is: the server would
  // refuse it, and a stale link should show the default view instead.
  const readMulti = (params) => {
    const read = {};
    for (const [name, value] of params) {
      if (!name.startsWith(FILTER_PREFIX)) continue;
      const key = name.slice(FILTER_PREFIX.length);
      if (!multiKeys.has(key)) continue;
      (read[key] ??= []).push(value);
    }
    return read;
  };

  const fromSearchParams = (params) => {
    const requested = params.get('sort');
    // A hand-edited or stale URL naming an unsupported column should show the default view, not
    // an error screen from a 400 the server is right to return.
    const sort = sortKeys.has(requested) ? requested : defaultQuery.sort;
    const direction = params.get('direction');

    return {
      sort,
      direction:
        direction === 'asc' || direction === 'desc' ? direction : defaultDirectionFor(sort),
      ...Object.fromEntries(filters.map((name) => [name, params.get(name) || null])),
      ...(multi ? { filters: readMulti(params) } : {}),
      cursor: params.get('cursor') || null,
    };
  };

  const appendMulti = (params, query) => {
    for (const [key, values] of Object.entries(query.filters ?? {})) {
      for (const value of values) params.append(FILTER_PREFIX + key, value);
    }
  };

  const toQueryString = (query) => {
    const params = new URLSearchParams();
    // Only non-default values, so the default view has a clean URL with nothing to explain.
    if (query.sort !== defaultQuery.sort) params.set('sort', query.sort);
    if (query.direction !== defaultDirectionFor(query.sort)) {
      params.set('direction', query.direction);
    }
    for (const name of filters) {
      if (query[name]) params.set(name, query[name]);
    }
    appendMulti(params, query);
    if (query.cursor) params.set('cursor', query.cursor);
    return params.toString();
  };

  // The filters alone: what the facets and the totals are read with, which changes with the
  // filter and never with the page.
  const filterSearch = (query) => {
    const params = new URLSearchParams();
    appendMulti(params, query);
    return params.toString();
  };

  const nextSort = (query, sort) => {
    const reversing = query.sort === sort;
    return {
      ...query,
      sort,
      direction: reversing
        ? query.direction === 'asc'
          ? 'desc'
          : 'asc'
        : defaultDirectionFor(sort),
      // A cursor encodes the ordering it was issued for and the server refuses it against
      // another, so re-sorting returns to the first page.
      cursor: null,
    };
  };

  const withFilter = (query, name, value) => ({ ...query, [name]: value || null, cursor: null });

  const withCursor = (query, cursor) => ({ ...query, cursor });

  // What the API client is given. Identical to what the URL shows, by construction - the sort is
  // merely re-encoded as `field:direction`, which is what the server's parse_sort accepts, while
  // the URL keeps them as two readable parameters.
  const toApiSearch = (query, limit) => {
    const params = new URLSearchParams(toQueryString(query));
    params.set('sort', `${query.sort}:${query.direction}`);
    params.delete('direction');
    if (limit) params.set('limit', String(limit));
    return params.toString();
  };

  // An empty set is no filter on that column. Any change drops the cursor: the server binds a
  // cursor to the filter set it was minted under. The key is one of the served stored columns
  // by construction - every caller is a control over those columns - so it is not guarded.
  const withValues = (query, key, values) => {
    const next = { ...query.filters };
    if (values.length) next[key] = values;
    else delete next[key];
    return { ...query, filters: next, cursor: null };
  };

  const clearFilters = (query) => ({ ...query, filters: {}, cursor: null });

  return {
    DEFAULT_QUERY: defaultQuery,
    defaultDirectionFor,
    fromSearchParams,
    toQueryString,
    filterSearch,
    nextSort,
    withFilter,
    withValues,
    clearFilters,
    withCursor,
    toApiSearch,
  };
}
